import { useState, useRef, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useCardsStore, useTopicsStore } from '@/stores'
import { parseContentSegments } from '@/utils/crossref'
import { formatLidForDisplay } from '@/utils/lid'
import { useKeyboardNavigation, type BoundaryDirection } from '@/hooks'
import { CardCarousel } from '@/components/CardCarousel'
import { EdgeCardPreview } from '@/components/EdgeCardPreview'
import './CardPage.css'

export default function CardPage() {
  const { lid } = useParams<{ lid: string }>()
  const decodedLid = lid ? decodeURIComponent(lid) : ''
  const navigate = useNavigate()
  const { getCardWithContext, updateCard, isLoading } = useCardsStore()
  const { getTopic } = useTopicsStore()

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editContent, setEditContent] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Action menu state
  const [menuOpen, setMenuOpen] = useState(false)
  const [showCardInfo, setShowCardInfo] = useState(false)
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
        setShowCardInfo(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Keyboard navigation
  const { navContext, boundaryHit } = useKeyboardNavigation(decodedLid)

  const cardContext = decodedLid ? getCardWithContext(decodedLid) : null

  // Keyboard shortcuts for actions (Shift+H/A/L/V/?)
  useEffect(() => {
    if (!cardContext) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if in input/textarea or editing
      const target = e.target as HTMLElement
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable ||
        isEditing
      ) {
        return
      }

      // Escape closes help modal
      if (e.key === 'Escape' && showShortcutsHelp) {
        setShowShortcutsHelp(false)
        return
      }

      // Shift key shortcuts
      if (e.shiftKey) {
        switch (e.key) {
          case '?':
            e.preventDefault()
            setShowShortcutsHelp((prev) => !prev)
            break
          case 'H':
            e.preventDefault()
            navigate('/dashboard')
            break
          case 'A':
            e.preventDefault()
            navigate(`/card/new?topic=${cardContext.topicId}&parent=${encodeURIComponent(cardContext.lid)}`)
            break
          case 'L':
            e.preventDefault()
            navigate(`/topic/${cardContext.topicId}`)
            break
          case 'V':
            e.preventDefault()
            navigate(`/topic/${cardContext.topicId}/canvas?focus=${encodeURIComponent(cardContext.lid)}`)
            break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [cardContext, isEditing, showShortcutsHelp, navigate])

  // Get boundary animation class
  const getBoundaryClass = (direction: BoundaryDirection): string => {
    if (!direction) return ''
    return `card-page--boundary-${direction}`
  }

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <div className="card-page-v2">
        <div className="card-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    )
  }

  if (!cardContext) {
    return (
      <div className="empty-state">
        <h3>Card not found</h3>
        <p>The card you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/dashboard" className="btn btn-primary">
          Back to Dashboard
        </Link>
      </div>
    )
  }

  const { parent, children, siblings, backlinks, ...card } = cardContext
  const topic = getTopic(card.topicId)

  // Parse content for cross-references
  const contentSegments = parseContentSegments(card.content)

  const handleStartEdit = () => {
    setEditTitle(card.title)
    setEditContent(card.content)
    setSaveError(null)
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setSaveError(null)
  }

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) return

    setIsSaving(true)
    setSaveError(null)

    try {
      await updateCard(card.lid, {
        title: editTitle.trim(),
        content: editContent,
      })
      setIsEditing(false)
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save changes')
    } finally {
      setIsSaving(false)
    }
  }

  const handleNavigate = (targetLid: string) => {
    navigate(`/card/${encodeURIComponent(targetLid)}`)
  }

  return (
    <div className={`card-page-v2 ${getBoundaryClass(boundaryHit)}`}>
      <div className="card-main-area">
        <div className="card-header">
          {topic && (
            <div className="card-topic-label">{topic.name}</div>
          )}
          <div className="card-title-row">
            <span className="lid-badge lid-square">{formatLidForDisplay(card.lid)}</span>
            {isEditing ? (
              <input
                type="text"
                className="input edit-title-input"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onInput={(e) => {
                  // Workaround for IME input scroll issue on Linux
                  const input = e.target as HTMLInputElement
                  const pos = input.selectionStart
                  if (pos !== null) {
                    requestAnimationFrame(() => {
                      input.setSelectionRange(pos, pos)
                    })
                  }
                }}
                placeholder="Card title..."
                autoFocus
              />
            ) : (
              <h1>{card.title}</h1>
            )}

            {/* Action Menu */}
            <div className="action-menu" ref={menuRef}>
              <button
                className="action-menu-trigger"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Card actions"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="12" cy="19" r="2" />
                </svg>
              </button>

              {menuOpen && (
                <div className="action-dropdown">
                  {!isEditing && (
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setMenuOpen(false)
                        handleStartEdit()
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit Card
                    </button>
                  )}
                  <Link
                    to={`/card/new?topic=${card.topicId}&parent=${encodeURIComponent(card.lid)}`}
                    className="dropdown-item"
                    onClick={() => setMenuOpen(false)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    Add Related Card
                  </Link>
                  <Link
                    to={`/topic/${card.topicId}`}
                    className="dropdown-item"
                    onClick={() => setMenuOpen(false)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="8" y1="6" x2="21" y2="6" />
                      <line x1="8" y1="12" x2="21" y2="12" />
                      <line x1="8" y1="18" x2="21" y2="18" />
                      <line x1="3" y1="6" x2="3.01" y2="6" />
                      <line x1="3" y1="12" x2="3.01" y2="12" />
                      <line x1="3" y1="18" x2="3.01" y2="18" />
                    </svg>
                    List View
                  </Link>
                  <Link
                    to={`/topic/${card.topicId}/canvas?focus=${encodeURIComponent(card.lid)}`}
                    className="dropdown-item"
                    onClick={() => setMenuOpen(false)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7" />
                      <rect x="14" y="3" width="7" height="7" />
                      <rect x="14" y="14" width="7" height="7" />
                      <rect x="3" y="14" width="7" height="7" />
                    </svg>
                    Canvas View
                  </Link>
                  <div className="dropdown-divider" />
                  <button
                    className="dropdown-item"
                    onClick={() => setShowCardInfo(!showCardInfo)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4M12 8h.01" />
                    </svg>
                    Card Info
                  </button>
                  {showCardInfo && (
                    <div className="dropdown-info">
                      <div className="dropdown-info-row">
                        <span className="dropdown-info-label">Created</span>
                        <span>{new Date(card.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="dropdown-info-row">
                        <span className="dropdown-info-label">Updated</span>
                        <span>{new Date(card.updatedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card-content">
          {isEditing ? (
            <div className="edit-content">
              <textarea
                className="input edit-content-input"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                placeholder="Write your note in Markdown. Use [[L-ID]] to create cross-references."
                rows={15}
              />
              <small className="form-hint">
                Supports Markdown. Use [[1/1a]] syntax to link to other cards.
              </small>
              {saveError && (
                <div className="form-error">{saveError}</div>
              )}
              <div className="edit-actions">
                <button
                  className="btn btn-secondary"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveEdit}
                  disabled={isSaving || !editTitle.trim()}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <div className="markdown-content">
              {contentSegments.map((segment, index) =>
                segment.type === 'crossref' ? (
                  <Link
                    key={index}
                    to={`/card/${encodeURIComponent(segment.lid!)}`}
                    className="cross-ref-link"
                  >
                    {formatLidForDisplay(segment.lid!)}
                  </Link>
                ) : (
                  <ReactMarkdown key={index} remarkPlugins={[remarkGfm]}>
                    {segment.content}
                  </ReactMarkdown>
                )
              )}
            </div>
          )}
        </div>

      </div>

      {/* Edge Card Navigation Previews */}
      {navContext && (
        <>
          <EdgeCardPreview
            card={navContext.parent}
            direction="left"
            onClick={() => navContext.parent && handleNavigate(navContext.parent.lid)}
          />
          <EdgeCardPreview
            card={navContext.firstChild}
            direction="right"
            onClick={() => navContext.firstChild && handleNavigate(navContext.firstChild.lid)}
          />
          <EdgeCardPreview
            card={navContext.prevSibling}
            direction="up"
            onClick={() => navContext.prevSibling && handleNavigate(navContext.prevSibling.lid)}
          />
          <EdgeCardPreview
            card={navContext.nextSibling}
            direction="down"
            onClick={() => navContext.nextSibling && handleNavigate(navContext.nextSibling.lid)}
          />
        </>
      )}

      <div className="card-carousel-area">
        <CardCarousel
          children={children}
          siblings={siblings}
          backlinks={backlinks}
          currentLid={card.lid}
          onNavigate={handleNavigate}
          showHelp={showShortcutsHelp}
          onToggleHelp={() => setShowShortcutsHelp((prev) => !prev)}
        />
      </div>
    </div>
  )
}
